import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { LoadingSpinner, useErrorHandler, useAuth0 } from '@ondeck/silkworm';
import AccountDetails from 'components/AccountDetails';
import useFetch from 'use-http';
import { getOfferDetails, postOffer, postOfferConfirm } from 'service';
import { useParams, useHistory } from 'react-router-dom';
import {
  DealProps,
  dealInitState,
  OfferDetail,
  offerDetailInitState,
  ModalType,
  OfferResponse,
  offerResponseInitState
} from 'states/initialState';
import Calculator from 'components/Calculator';
import LineOfCredit from 'components/LineOfCredit';
import SessionTimeout from 'components/SessionTimeout';
import MerchantDetails from 'components/MerchantDetails';
import ClosingDocs from 'components/ClosingDocs';
import SelectedDeal from 'components/SelectedDeal';
import ConfirmationModal from 'components/ConfirmationModal';
import ViewDeal from 'components/ViewDeal';
import { useAppData } from '../contexts/appContext';
import { Tabs, Tab, Card, Row, Col, Stack, Accordion } from 'react-bootstrap';
import { useTracking } from 'react-tracking';
import { fetchData } from 'service/fetchData';
import { datadogRum } from '@datadog/browser-rum';

type AppParams = {
  businessId: string;
};

const ApplicationPage = () => {
  const { Track, trackEvent } = useTracking(
    { event: 'Page Load', page: 'Application Page' },
    { dispatchOnMount: true }
  );

  const [isLoading, setIsLoading] = useState(true);
  const [offerDetails, setOfferDetails] = useState<OfferDetail>(
    offerDetailInitState
  );
  const [pricedDeal, setPricedDeal] = useState<DealProps>(dealInitState);
  const [dealChanged, setDealChanged] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const handleCloseConfirmModal = () =>
    !sendingConfirmation && setShowConfirmModal(false);
  const handleShowConfirmModal = () => setShowConfirmModal(true);
  const [modalType, setModalType] = useState<ModalType>(ModalType.LOC);
  const [sendingConfirmation, setSendingConfirmation] = useState(false);
  const [fixedCommission, setFixedCommission] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<OfferResponse>(
    offerResponseInitState
  );

  const { user, getTokenSilently } = useAuth0();
  const { handleError } = useErrorHandler();
  const { businessId } = useParams<AppParams>();
  const { updatePartnerDetails, updateAlert } = useAppData();
  const hook = useFetch(window.env.REACT_APP_PARTNER_ORCHESTRATOR, {
    cacheLife: 5000,
    retryDelay: 30000,
    headers: {
      partnerContactEmail: `${user.email}`
    }
  });

  const history = useHistory();
  const url = `${history.location.state?.from.pathname || '/'}`;
  if (url.includes('/')) {
    sessionStorage.setItem('url', '/');
  }

  useEffect(() => {
    (async () => {
      try {
        const o = await getOfferDetails(hook, businessId);
        if (o.status) {
          handleError(o);
          return;
        }
        if (!o.customerData.onlineCheckoutEligible) {
          updateAlert('offerConfirm.alert.danger.application');
        }
        setOfferDetails(o);
        let deal = selectedDeal;
        deal.customerData = o.customerData;
        deal.applicationType = o.applicationType;
        setSelectedDeal(deal);
        updatePartnerDetails(o.partnerDetails);
        setIsLoading(false);

        trackEvent({
          event: 'View DIY Offer',
          userProfileName: o.partnerDetails.partner,
          opportunityId: businessId,
          termLoanMaxApproval: o.offer?.maxLineAmount,
          locMaxApproval: o.lineOfCreditOffer?.lineAmount
        });
      } catch (e) {
        handleError(e);
        datadogRum.addError(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePriceDeal = async ({
    loanSpecifics,
    offerId,
    commissionAmount
  }) => {
    let deal = selectedDeal;
    deal.loanSpecifics = loanSpecifics;
    deal.offerId = offerId;
    setSelectedDeal(deal);

    const d = await postOffer(hook, selectedDeal);
    if (d.status) {
      updateAlert('pricing.alert.danger.application');
      return;
    }

    d.apr = Math.round(d.apr * 100) / 100;
    d.commission = commissionAmount;

    setPricedDeal(d);
    setDealChanged(false);
  };

  const sendDisclosure = async data =>
    fetchData({
      method: 'post',
      body: data,
      endPoint: `${window.env.REACT_APP_PARTNER_ORCHESTRATOR}/partner/application/${businessId}/disclosure`,
      token: await getTokenSilently(),
      headers: { partnerContactEmail: user.email },
      onError: () => {
        updateAlert('disclosure.alert.danger.error');
      }
    });

  const handleConfirmation = async (
    { loanSpecifics, offerId },
    pricedDeal,
    locOffer
  ) => {
    setSendingConfirmation(true);
    let deal = selectedDeal;
    deal.loanSpecifics = loanSpecifics;
    deal.offerId = offerId;
    setSelectedDeal(deal);
    const response = await postOfferConfirm(hook, businessId, selectedDeal);

    if (!response?.status) {
      let offer =
        loanSpecifics[0].productType === 'LOC' ? locOffer : pricedDeal;
      let paymentFrequency =
        loanSpecifics[0].productType === 'LOC'
          ? offer?.paymentFrequency[0]
          : offer?.paymentFrequency;
      let amount =
        loanSpecifics[0].productType === 'LOC'
          ? offer?.lineAmount
          : offer?.loanAmount;
      let productType = loanSpecifics[0].productType === 'LOC' ? 'LOC' : 'Term';
      await sendDisclosure({
        loanAmount: +amount,
        disbursementAmount: offer?.disbursementAmount,
        lender: offer?.lender,
        apr: offer?.apr,
        totalRepaymentAmount: offer?.totalAmountPaidBack,
        averageMonthlyPayment: offer?.averageMonthlyPayment,
        paymentAmount: offer?.payment,
        paymentFrequency: paymentFrequency,
        termMonths: offer?.term,
        originationFee: offer?.originationFee,
        totalInterest: offer?.totalInterest,
        prepaymentDiscountPercentage:
          loanSpecifics[0]?.prepaymentDiscountPercentage,
        productType: productType,
        trueAIR: offer?.trueAIR,
        totalDrawCost: offer?.totalDrawCost,
        numberOfPayments: offer?.numberOfPayments,
        offerId: offer?.offerId
      });
    }

    setShowConfirmModal(false);
    if (response?.status) {
      trackEvent({
        event: 'Checkout Error',
        opportunityId: businessId
      });
      updateAlert('offerConfirm.alert.danger.merchant');
    } else updateAlert('offerConfirm.alert.success.checkout');
    setSendingConfirmation(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return isLoading ? (
    <>
      <LoadingSpinner>
        <FormattedMessage id="common.loading.message" />
      </LoadingSpinner>
    </>
  ) : (
    <>
      <Track>
        <ConfirmationModal
          selectedDeal={selectedDeal}
          sendingConfirmation={sendingConfirmation}
          deal={pricedDeal}
          loc={offerDetails.lineOfCreditOffer}
          show={showConfirmModal}
          handleClose={handleCloseConfirmModal}
          handleConfirmation={handleConfirmation}
          email={offerDetails.customerData.email}
          modalType={modalType}
          businessId={businessId}
        />
        <Stack gap={4}>
          <MerchantDetails
            offer={offerDetails.offer}
            customerData={offerDetails.customerData}
            lineOfCreditOffer={offerDetails.lineOfCreditOffer}
            businessId={businessId}
          />
          <div>
            <Tabs
              mountOnEnter={true}
              onSelect={tab => {
                trackEvent({
                  event: 'Product Tab Selected',
                  product: tab,
                  opportunityId: businessId
                });
              }}
            >
              {offerDetails.offer.terms && (
                <Tab
                  eventKey="term"
                  title="Term Loan"
                  id="term"
                  aria-controls="term"
                  aria-labelledby="term"
                >
                  <Row className="m-0">
                    <Col xs={12} className="p-4 col-md">
                      <Calculator
                        onlineCheckoutEligible={
                          offerDetails.customerData.onlineCheckoutEligible
                        }
                        businessId={businessId}
                        offer={offerDetails.offer}
                        setDealChanged={setDealChanged}
                        handlePriceDeal={handlePriceDeal}
                        setFixedCommission={setFixedCommission}
                        fixedCommission={fixedCommission}
                      />
                    </Col>
                    {pricedDeal.loanAmount > 0 && (
                      <Col
                        xs={12}
                        md={4}
                        className="border border-2 border-secondary rounded-lg-end py-4 px-0"
                      >
                        <SelectedDeal
                          deal={pricedDeal}
                          onlineCheckoutEligible={
                            offerDetails.customerData.onlineCheckoutEligible
                          }
                          maxLoanAmount={offerDetails.offer?.maxLineAmount}
                          disableCheckout={
                            !offerDetails.customerData.onlineCheckoutEligible ||
                            dealChanged
                          }
                          handleShow={handleShowConfirmModal}
                          setModalType={setModalType}
                          fixedCommission={fixedCommission}
                        />
                      </Col>
                    )}
                  </Row>
                </Tab>
              )}
              {offerDetails.lineOfCreditOffer && (
                <Tab
                  eventKey="loc"
                  title="Line of Credit"
                  id="loc"
                  aria-controls="loc"
                  aria-labelledby="loc"
                >
                  <LineOfCredit
                    offer={offerDetails.lineOfCreditOffer}
                    disableCheckout={
                      !offerDetails.customerData.onlineCheckoutEligible
                    }
                    handleShow={handleShowConfirmModal}
                    setModalType={setModalType}
                  />
                </Tab>
              )}
            </Tabs>
          </div>
          <ClosingDocs />
          <AccountDetails
            accountManagerDetails={offerDetails.accountManagerDetails}
          />
          {pricedDeal.loanAmount > 0 && (
            <>
              <Card className="mt-4">
                <AccountDetails
                  accountManagerDetails={offerDetails.accountManagerDetails}
                />
              </Card>
              <Accordion
                className="fixed-bottom accordion-secondary d-block d-md-none"
                defaultActiveKey="0"
              >
                <Accordion.Item eventKey="0">
                  <Accordion.Header>View Deal</Accordion.Header>
                  <Accordion.Body>
                    <ViewDeal
                      deal={pricedDeal}
                      dealChanged={dealChanged}
                      handleShow={handleShowConfirmModal}
                      setModalType={setModalType}
                    />
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </>
          )}
        </Stack>
      </Track>
      <SessionTimeout />
    </>
  );
};

export default ApplicationPage;
